import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Dot,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('shaheen_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

const s = {
  page: { padding: '20px 16px', overflowY: 'auto', flex: 1 },
  title: { fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 },
  sub: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10, marginBottom: 20 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 },
  metric: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px' },
  mLabel: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 6 },
  mValue: { fontSize: 24, fontWeight: 500, color: 'var(--gold)' },
  mSub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  card: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px', cursor: 'pointer', transition: 'border-color 0.12s' },
  cardTitle: { fontSize: 13, fontWeight: 500, color: 'var(--gold)', marginBottom: 6 },
  cardDesc: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 },
  tag: { display: 'inline-block', marginTop: 10, fontSize: 10, background: 'var(--bg-raised)', color: 'var(--text-muted)', border: '0.5px solid var(--border)', padding: '3px 8px', borderRadius: 8 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 },
  sev: { width: 5, borderRadius: 3, flexShrink: 0, alignSelf: 'stretch' },
  info: { flex: 1, minWidth: 0 },
  iTitle: { fontSize: 13, color: 'var(--text-primary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  iMeta: { fontSize: 11, color: 'var(--text-muted)' },
  badge: { fontSize: 10, padding: '3px 8px', borderRadius: 7, flexShrink: 0 },
  scoreCircle: { width: 80, height: 80, borderRadius: '50%', border: '3px solid var(--gold)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  scanRow: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 10 },
  scanHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  scanTitle: { fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' },
  scanDate: { fontSize: 11, color: 'var(--text-muted)' },
  flag: { fontSize: 12, color: 'var(--text-secondary)', padding: '5px 0', borderBottom: '0.5px solid var(--border)' },
  empty: { textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 },
  requestBtn: { background: 'var(--gold)', color: 'var(--bg-base)', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginTop: 14, width: '100%' },
  loading: { color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' },
};

const sevColor = { CRITICAL: 'var(--red)', HIGH: 'var(--amber)', MEDIUM: 'var(--blue)', LOW: 'var(--green)' };
const statusStyle = {
  OPEN: { background: 'var(--amber-bg)', color: 'var(--amber)' },
  CLOSED: { background: 'var(--green-bg)', color: 'var(--green)' },
};

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ts.slice(0, 10); }
}

const SERVICES = [
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/><path d="M8 11h6M11 8v6"/></svg>,
    name: 'Digital exposure check',
    desc: 'Full assessment across the web, social media, and paste sites for any leaked information about you',
    tag: 'On demand', msg: 'Please run a full digital exposure check on me'
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M9 9h.01M15 9h.01M9.5 13.5s1 1.5 2.5 1.5 2.5-1.5 2.5-1.5"/><path d="M2 12h3M19 12h3M12 2v3M12 19v3"/></svg>,
    name: 'Dark web check',
    desc: 'Search breach databases and dark web sources for your email addresses and personal data',
    tag: 'On demand', msg: 'Run a dark web check on my accounts'
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>,
    name: 'Personal security briefing',
    desc: 'A personalized threat intelligence report based on your sector, role, and recent activity',
    tag: 'On demand', msg: 'Generate a personal security briefing for me'
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>,
    name: 'Travel security advisory',
    desc: 'Before any trip — destination risk level, safe connectivity, and what to prepare on your devices',
    tag: 'On demand', msg: 'I need travel security advice for an upcoming trip'
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/><path d="M9 7h6M9 11h6M9 15h4"/></svg>,
    name: 'Device security setup',
    desc: 'Step-by-step hardening for your phone, laptop, and home network — tailored to your devices',
    tag: 'Guided', msg: 'Help me set up my devices securely'
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>,
    name: 'Emergency assistance',
    desc: 'Immediate guided response for any active security incident — account breach, fraud, or device issue',
    tag: 'Priority', msg: 'I need emergency assistance with a security issue'
  },
];

function MonitoringPopup({ onClose }) {
  const { client } = useAuth();
  const [form, setForm] = useState({ email1: '', email2: '', linkedin: '', twitter: '', instagram: '', domain: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setError('');
    const emails = [form.email1, form.email2].filter(e => e.trim());
    const social = {};
    if (form.linkedin.trim()) social.linkedin = form.linkedin.trim().replace('@','');
    if (form.twitter.trim()) social.twitter = form.twitter.trim().replace('@','');
    if (form.instagram.trim()) social.instagram = form.instagram.trim().replace('@','');
    try {
      const res = await authFetch(`${API}/client/${client?.id}/monitoring`, {
        method: 'POST',
        body: JSON.stringify({ emails_to_monitor: emails, social_usernames: social, company_domain: form.domain.trim() }),
      });
      if (!res.ok) throw new Error('Failed to save');
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch (e) { setError('Could not save. Please try again.'); }
    finally { setSaving(false); }
  };

  const ps = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(8,8,15,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 },
    modal: { width: '100%', maxWidth: 480, background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 14, padding: '24px' },
    title: { fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 },
    sub: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 },
    label: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 5, display: 'block' },
    field: { marginBottom: 12 },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
    section: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em', margin: '16px 0 10px', paddingTop: 12, borderTop: '0.5px solid var(--border)' },
    row: { display: 'flex', gap: 10, marginTop: 16 },
    cancelBtn: { flex: 1, background: 'var(--bg-raised)', border: '0.5px solid var(--border)', color: 'var(--text-secondary)', borderRadius: 8, padding: '10px', fontSize: 13, cursor: 'pointer' },
    saveBtn: { flex: 1, background: 'var(--gold)', color: 'var(--bg-base)', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
    err: { fontSize: 12, color: 'var(--red)', background: 'var(--red-bg)', padding: '8px 12px', borderRadius: 6, marginTop: 8 },
    ok: { fontSize: 12, color: 'var(--green)', background: 'var(--green-bg)', padding: '8px 12px', borderRadius: 6, marginTop: 8 },
  };

  return (
    <div style={ps.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={ps.modal}>
        <div style={ps.title}>Update monitoring details</div>
        <div style={ps.sub}>Add the email addresses and social media accounts you want Shaheen to monitor for breaches and impersonation.</div>
        <div style={ps.section}>EMAIL ADDRESSES</div>
        <div style={ps.grid2}>
          <div><span style={ps.label}>WORK EMAIL</span><input value={form.email1} onChange={e => f('email1', e.target.value)} placeholder="you@company.com" /></div>
          <div><span style={ps.label}>PERSONAL EMAIL</span><input value={form.email2} onChange={e => f('email2', e.target.value)} placeholder="you@gmail.com" /></div>
        </div>
        <div style={ps.section}>SOCIAL MEDIA USERNAMES</div>
        <div style={ps.grid2}>
          <div><span style={ps.label}>LINKEDIN</span><input value={form.linkedin} onChange={e => f('linkedin', e.target.value)} placeholder="yourname" /></div>
          <div><span style={ps.label}>TWITTER / X</span><input value={form.twitter} onChange={e => f('twitter', e.target.value)} placeholder="@yourname" /></div>
        </div>
        <div style={ps.field}><span style={ps.label}>INSTAGRAM</span><input value={form.instagram} onChange={e => f('instagram', e.target.value)} placeholder="@yourname" /></div>
        <div style={ps.field}><span style={ps.label}>COMPANY DOMAIN (optional)</span><input value={form.domain} onChange={e => f('domain', e.target.value)} placeholder="yourcompany.com" /></div>
        {error && <div style={ps.err}>{error}</div>}
        {saved && <div style={ps.ok}>✓ Monitoring details saved successfully</div>}
        <div style={ps.row}>
          <button style={ps.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={ps.saveBtn} onClick={save} disabled={saving || saved}>{saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save & activate'}</button>
        </div>
      </div>
    </div>
  );
}

export function Services() {
  const nav = useNavigate();
  const [showMonitoring, setShowMonitoring] = useState(false);
  const go = (msg) => { localStorage.setItem('shaheen_prefill', msg); nav('/chat'); };
  return (
    <div style={s.page}>
      {showMonitoring && <MonitoringPopup onClose={() => setShowMonitoring(false)} />}
      <div style={s.title}>Request a service</div>
      <div style={s.sub}>Select a service and your concierge will guide you through it</div>
      <div style={s.grid3}>
        {SERVICES.map(sv => (
          <div key={sv.name} style={s.card} onClick={() => go(sv.msg)}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ marginBottom: 10, color: 'var(--gold)' }}>{sv.icon}</div>
            <div style={s.cardTitle}>{sv.name}</div>
            <div style={s.cardDesc}>{sv.desc}</div>
            <div style={s.tag}>{sv.tag}</div>
          </div>
        ))}
        <div style={s.card} onClick={() => setShowMonitoring(true)}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
          <div style={{ marginBottom: 10, color: 'var(--gold)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          </div>
          <div style={s.cardTitle}>Update my monitoring</div>
          <div style={s.cardDesc}>Add your email addresses, social media accounts, and company domain to your protection profile</div>
          <div style={s.tag}>Profile</div>
        </div>
      </div>
    </div>
  );
}

export function Incidents() {
  const { client } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client?.id) return;
    authFetch(`${API}/client/${client.id}/cases`)
      .then(r => r.json())
      .then(d => setCases(d.cases || []))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, [client]);

  return (
    <div style={s.page}>
      <div style={s.title}>My cases</div>
      <div style={s.sub}>Your active and resolved security cases</div>
      {loading ? <div style={s.loading}>One moment...</div> :
       cases.length === 0 ? (
        <div style={s.empty}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>No active cases</div>
          <div style={{ fontSize: 12 }}>No active cases. Your digital environment is being monitored around the clock.</div>
          <button style={s.requestBtn} onClick={() => nav('/chat')}>Report an incident</button>
        </div>
      ) : (
        <div style={s.list}>
          {cases.map(c => (
            <div key={c.case_id} style={s.row}>
              <div style={{ ...s.sev, background: sevColor[c.severity] || 'var(--text-muted)' }} />
              <div style={s.info}>
                <div style={s.iTitle}>{(c.incident_type || '').replace(/_/g, ' ')}</div>
                <div style={s.iMeta}>{c.case_id} — {fmt(c.created_at)}</div>
              </div>
              <div style={{ ...s.badge, ...statusStyle[c.status] || { background: 'var(--bg-raised)', color: 'var(--text-muted)' } }}>
                {c.status}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Risk Score Timeline helpers ────────────────────────────────────────────

function scoreColor(v) {
  return v >= 75 ? '#22c55e' : v >= 50 ? '#eab308' : '#ef4444';
}

function ScoreTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '0.5px solid var(--border)',
      borderRadius: 8, padding: '10px 14px', maxWidth: 230,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: scoreColor(d.score) }}>{d.score}</span>
        <span style={{ fontSize: 13, color: scoreColor(d.score), fontWeight: 600 }}>Grade {d.grade}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: d.summary ? 6 : 0 }}>{d.dateLabel}</div>
      {d.summary && (
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{d.summary}</div>
      )}
    </div>
  );
}

function ScoreDot(props) {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  return (
    <Dot
      cx={cx} cy={cy} r={5}
      fill={scoreColor(payload.score)}
      stroke="var(--bg-base)"
      strokeWidth={2}
    />
  );
}

const FACTOR_ROWS = [
  {
    key: 'breach_count',
    label: 'Active breaches',
    render: (v) => v > 0
      ? { text: `${v} found`, impact: `-${Math.min(v * 10, 30)} pts`, bad: true }
      : { text: 'None detected', impact: 'No deduction', bad: false },
  },
  {
    key: 'osint_exposure',
    label: 'OSINT exposure',
    render: (v) => {
      const map = { high: { text: 'High', impact: '-15 pts', bad: true }, medium: { text: 'Medium', impact: '-8 pts', bad: true }, low: { text: 'Low', impact: 'No deduction', bad: false }, none: { text: 'None', impact: 'No deduction', bad: false } };
      return map[v] || { text: v || 'Unknown', impact: '—', bad: false };
    },
  },
  {
    key: 'incident_count',
    label: 'Open incidents',
    render: (v) => v > 0
      ? { text: `${v} open`, impact: `-${Math.min(v * 12, 24)} pts`, bad: true }
      : { text: 'None', impact: 'No deduction', bad: false },
  },
  {
    key: 'scan_age_days',
    label: 'Assessment coverage',
    render: (v) => {
      if (v == null) return { text: 'No assessment on record', impact: '-5 pts', bad: true };
      if (v >= 60) return { text: `${v} days old`, impact: '-10 pts', bad: true };
      if (v >= 30) return { text: `${v} days old`, impact: '-5 pts', bad: true };
      return { text: `${v} days old`, impact: 'No deduction', bad: false };
    },
  },
];

function FactorsBreakdown({ factors, score, grade }) {
  if (!factors) return null;
  const gc = scoreColor(score);
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%',
          border: `3px solid ${gc}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: gc, lineHeight: 1 }}>{grade}</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: gc }}>{score}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Current risk score</div>
        </div>
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em', marginBottom: 8 }}>SCORE FACTORS</div>
      {FACTOR_ROWS.map(({ key, label, render }) => {
        const cell = render(factors[key]);
        return (
          <div key={key} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '9px 0', borderBottom: '0.5px solid var(--border)', gap: 10,
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: cell.bad ? 'var(--red)' : 'var(--text-muted)' }}>{cell.text}</span>
              <span style={{
                fontSize: 11, fontFamily: 'monospace',
                color: cell.bad ? 'var(--amber)' : 'var(--green)',
                minWidth: 80, textAlign: 'right',
              }}>
                {cell.impact}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Expandable finding cards inside a scan ─────────────────────────────────

function buildFindings(scan) {
  const findings = [];
  const risk = scan.risk_summary || {};

  // Breaches — one card per checked email
  const checks = (scan.breach_checks || []).filter(c => c.status !== 'skipped');
  checks.forEach(c => {
    if (c.found) {
      findings.push({
        key: `breach_${c.email || c.query}`,
        severity: 'high',
        icon: '⚠️',
        summary: `Found in ${c.breach_count || c.total || 1} data breach${(c.breach_count || c.total || 1) !== 1 ? 'es' : ''} — ${c.email || c.query}`,
        detail: { type: 'breach', check: c },
      });
    } else {
      findings.push({
        key: `ok_${c.email || c.query}`,
        severity: 'ok',
        icon: '✓',
        summary: `No breaches found — ${c.email || c.query}`,
        detail: null,
      });
    }
  });
  if (!checks.length) {
    findings.push({ key: 'breach_pending', severity: 'info', icon: '○', summary: 'Monitoring is being activated. Check back shortly.', detail: null });
  }

  // Impersonation
  if ((scan.impersonation?.suspicious_count || 0) > 0) {
    findings.push({
      key: 'impersonation',
      severity: 'critical',
      icon: '🚨',
      summary: `${scan.impersonation.suspicious_count} potential impersonation account${scan.impersonation.suspicious_count !== 1 ? 's' : ''} detected`,
      detail: { type: 'impersonation', data: scan.impersonation },
    });
  }

  // Social presence
  if ((scan.social_presence || []).length > 0) {
    const total = scan.social_presence.reduce((n, p) => n + (p.found_on?.length || 0), 0);
    if (total > 0) {
      findings.push({
        key: 'social',
        severity: total >= 6 ? 'medium' : 'low',
        icon: '🌐',
        summary: `Active on ${total} social platform${total !== 1 ? 's' : ''} — ${total >= 6 ? 'elevated' : 'moderate'} exposure`,
        detail: { type: 'social', data: scan.social_presence },
      });
    }
  }

  // News mentions
  const news = scan.news_mentions;
  if (news?.mention_count > 0) {
    findings.push({
      key: 'news',
      severity: 'info',
      icon: '📰',
      summary: `${news.mention_count} recent news mention${news.mention_count !== 1 ? 's' : ''} — high media visibility`,
      detail: { type: 'news', data: news },
    });
  }

  // Any remaining flags not already covered
  const covered = ['breach', 'impersonation', 'social', 'news', 'media'];
  (risk.flags || [])
    .filter(f => !covered.some(kw => f.toLowerCase().includes(kw)))
    .forEach((f, i) => {
      findings.push({ key: `flag_${i}`, severity: 'info', icon: '•', summary: f, detail: null });
    });

  return findings;
}

function FindingDetail({ detail }) {
  if (!detail) return null;

  if (detail.type === 'breach') {
    const c = detail.check;
    const breaches = c.breaches || [];
    return (
      <div style={{ paddingTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 8 }}>BREACH DETAILS</div>
        {breaches.length > 0 ? breaches.map((b, i) => (
          <div key={i} style={{ marginBottom: 10, background: 'var(--bg-raised)', borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--red)', marginBottom: 4 }}>{b.name}</div>
            {b.breach_date && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Date: {b.breach_date}</div>}
            {b.description && <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{b.description.slice(0, 200)}</div>}
            {b.data_classes?.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                {b.data_classes.slice(0, 5).map(d => (
                  <span key={d} style={{ fontSize: 10, padding: '2px 6px', background: 'rgba(239,68,68,0.1)', color: 'var(--red)', borderRadius: 4 }}>{d}</span>
                ))}
              </div>
            )}
          </div>
        )) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Breach confirmed. Detailed record unavailable from this source.</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--amber)', background: 'rgba(245,158,11,0.08)', borderRadius: 6, padding: '8px 10px', marginTop: 6 }}>
          Action: Change the password for this email on all sites where it is used.
        </div>
      </div>
    );
  }

  if (detail.type === 'impersonation') {
    const accounts = detail.data.suspicious_accounts || [];
    return (
      <div style={{ paddingTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 8 }}>SUSPICIOUS ACCOUNTS</div>
        {accounts.length > 0 ? accounts.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 80, flexShrink: 0 }}>{a.platform}</span>
            <span style={{ fontSize: 12, color: 'var(--text-primary)', flex: 1 }}>@{a.username}</span>
            {a.url && (
              <a href={a.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--gold)', textDecoration: 'none', flexShrink: 0 }}>View ↗</a>
            )}
          </div>
        )) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Account list unavailable. Contact your concierge to review.</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--red)', background: 'rgba(239,68,68,0.08)', borderRadius: 6, padding: '8px 10px', marginTop: 8 }}>
          Action: Forward this list to your concierge — they can request takedowns on your behalf.
        </div>
      </div>
    );
  }

  if (detail.type === 'social') {
    return (
      <div style={{ paddingTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 8 }}>PLATFORMS FOUND</div>
        {detail.data.map((presence, i) => (
          presence.found_on?.length > 0 && (
            <div key={i} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Username: @{presence.username}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {presence.found_on.map(f => (
                  <a key={f.platform} href={f.url} target="_blank" rel="noreferrer"
                    style={{ fontSize: 11, padding: '3px 10px', background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 6, color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {f.platform} ↗
                  </a>
                ))}
              </div>
            </div>
          )
        ))}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, fontStyle: 'italic' }}>
          High platform count increases your public footprint. Ask your concierge which accounts to make private.
        </div>
      </div>
    );
  }

  if (detail.type === 'news') {
    const mentions = detail.data.mentions || [];
    return (
      <div style={{ paddingTop: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 8 }}>RECENT MENTIONS</div>
        {mentions.map((m, i) => (
          <div key={i} style={{ padding: '8px 0', borderBottom: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{m.title}</div>
            {m.published && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{m.published}</div>}
          </div>
        ))}
        {!mentions.length && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Titles unavailable — check your portal for the full report.</div>}
      </div>
    );
  }

  return null;
}

function ScanFindings({ scan }) {
  const [openKey, setOpenKey] = useState(null);
  const findings = buildFindings(scan);

  const sevColor = {
    critical: 'var(--red)', high: 'var(--red)',
    medium: 'var(--amber)', low: 'var(--green)',
    ok: 'var(--green)', info: 'var(--text-muted)',
  };
  const sevBg = {
    critical: 'rgba(239,68,68,0.08)', high: 'rgba(239,68,68,0.06)',
    medium: 'rgba(245,158,11,0.07)', low: 'rgba(34,197,94,0.07)',
    ok: 'rgba(34,197,94,0.07)', info: 'transparent',
  };

  return (
    <div style={{ paddingTop: 4 }}>
      {findings.map(f => {
        const isOpen = openKey === f.key;
        const hasDetail = !!f.detail;
        const col = sevColor[f.severity] || 'var(--text-muted)';
        const bg  = sevBg[f.severity]   || 'transparent';
        return (
          <div key={f.key} style={{ borderRadius: 8, background: bg, border: `0.5px solid ${isOpen ? col + '66' : 'transparent'}`, marginBottom: 6, overflow: 'hidden' }}>
            <div
              onClick={() => hasDetail && setOpenKey(isOpen ? null : f.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: hasDetail ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 15, flexShrink: 0 }}>{f.icon}</span>
              <span style={{ flex: 1, fontSize: 12, color: f.severity === 'ok' ? 'var(--text-muted)' : 'var(--text-primary)', lineHeight: 1.5 }}>
                {f.summary}
              </span>
              {hasDetail && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2" strokeLinecap="round"
                  style={{ flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              )}
            </div>
            {isOpen && (
              <div style={{ padding: '0 12px 12px', borderTop: `0.5px solid ${col}33` }}>
                <FindingDetail detail={f.detail} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function Reports() {
  const { client } = useAuth();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [scans, setScans] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [riskScores, setRiskScores] = useState([]);
  const [latestRisk, setLatestRisk] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState('scans');

  useEffect(() => {
    if (!client?.id) return;
    Promise.all([
      authFetch(`${API}/client/${client.id}/health`).then(r => r.json()).catch(() => null),
      authFetch(`${API}/client/${client.id}/scans`).then(r => r.json()).catch(() => ({ scans: [] })),
      authFetch(`${API}/client/${client.id}/briefings`).then(r => r.json()).catch(() => ({ briefings: [] })),
      authFetch(`${API}/client/${client.id}/risk-scores`).then(r => r.json()).catch(() => ({ scores: [] })),
      authFetch(`${API}/client/${client.id}/risk-score/latest`).then(r => r.ok ? r.json() : null).catch(() => null),
      authFetch(`${API}/client/${client.id}/threat-watchlist`).then(r => r.json()).catch(() => ({ watchlist: [] })),
    ]).then(([h, sc, b, rs, lr, wl]) => {
      setHealth(h?.health_score !== undefined ? h : null);
      setScans(sc?.scans || []);
      setBriefings(b?.briefings || []);
      setRiskScores(rs?.scores || []);
      setLatestRisk(lr?.score !== undefined ? lr : null);
      setWatchlist(wl?.watchlist || []);
    }).finally(() => setLoading(false));
  }, [client]);

  // Prepare chart data — add readable date label to each entry
  const chartData = riskScores.map(entry => {
    const d = new Date(entry.created_at.endsWith('Z') ? entry.created_at : entry.created_at + 'Z');
    return {
      ...entry,
      dateLabel: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }),
      shortDate: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    };
  });

  const displayScore = latestRisk?.score ?? health?.health_score;
  const displayGrade = latestRisk?.grade ?? health?.grade;
  const displayStatus = latestRisk
    ? (latestRisk.score >= 75 ? 'Strong' : latestRisk.score >= 60 ? 'Moderate' : 'At risk')
    : health?.status;
  const sc = displayScore !== undefined
    ? (displayScore >= 75 ? 'var(--green)' : displayScore >= 55 ? 'var(--amber)' : 'var(--red)')
    : 'var(--text-muted)';

  const tabStyle = (active) => ({
    fontSize: 12, fontWeight: active ? 500 : 400,
    color: active ? 'var(--gold)' : 'var(--text-muted)',
    background: active ? 'var(--gold-dim)' : 'transparent',
    border: `0.5px solid ${active ? 'var(--gold)' : 'var(--border)'}`,
    borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
  });

  return (
    <div style={s.page}>
      <div style={s.title}>Security reports</div>
      <div style={s.sub}>Your security posture and assessment history</div>

      {loading ? <div style={s.loading}>Preparing your security briefing...</div> : (
        <>
          {/* Score summary row */}
          {(displayScore !== undefined || health) && (
            <div style={{ ...s.row, marginBottom: 20, alignItems: 'flex-start' }}>
              <div style={{ ...s.scoreCircle, borderColor: sc }}>
                <div style={{ fontSize: 22, fontWeight: 500, color: sc }}>{displayScore ?? '—'}</div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: '0.06em' }}>SCORE</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: sc, marginBottom: 4 }}>
                  {displayGrade ? `Grade ${displayGrade} — ` : ''}{displayStatus || ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.5 }}>
                  {health?.open_cases > 0 ? `${health.open_cases} open case${health.open_cases > 1 ? 's' : ''} — ` : 'No open cases — '}
                  {health?.last_scan ? `Last assessment: ${fmt(health.last_scan)}` : 'No assessments yet'}
                </div>
                <button style={{ ...s.requestBtn, marginTop: 0, width: 'auto', padding: '7px 16px', fontSize: 12 }}
                  onClick={() => nav('/chat')}>
                  Request an assessment
                </button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <button style={tabStyle(tab === 'scans')} onClick={() => setTab('scans')}>
              Assessments {scans.length > 0 && `(${scans.length})`}
            </button>
            <button style={tabStyle(tab === 'briefings')} onClick={() => setTab('briefings')}>
              Briefings {briefings.length > 0 && `(${briefings.length})`}
            </button>
            <button style={tabStyle(tab === 'timeline')} onClick={() => setTab('timeline')}>
              Score timeline
            </button>
            <button style={tabStyle(tab === 'threats')} onClick={() => setTab('threats')}>
              Region threats {watchlist.length > 0 && `(${watchlist.length})`}
            </button>
          </div>

          {/* Scans tab */}
          {tab === 'scans' && (
            scans.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No assessments on record yet</div>
                <div style={{ fontSize: 12, marginBottom: 14 }}>No assessments on record yet. Request a digital exposure assessment to see your results here.</div>
                <button style={{ ...s.requestBtn, width: 'auto', padding: '9px 20px' }}
                  onClick={() => { localStorage.setItem('shaheen_prefill', 'Run a digital exposure check on me'); nav('/chat'); }}>
                  Request my first assessment
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  {scans.length} assessment{scans.length > 1 ? 's' : ''} on record
                </div>
                {scans.map((scan, i) => {
                  const risk = scan.risk_summary || {};
                  const isOpen = expanded === i;
                  const riskColor = risk.level === 'HIGH' ? 'var(--red)' : risk.level === 'MEDIUM' ? 'var(--amber)' : 'var(--green)';
                  return (
                    <div key={i} style={s.scanRow}>
                      <div style={s.scanHeader} onClick={() => setExpanded(isOpen ? null : i)}>
                        <div>
                          <div style={s.scanTitle}>Digital exposure check</div>
                          <div style={s.scanDate}>{fmt(scan.scan_timestamp)}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: riskColor, fontWeight: 500 }}>
                            {risk.level || 'Unknown'} risk
                          </span>
                          <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>

                      {isOpen && (
                        <div style={{ padding: '4px 0 8px' }}>
                          <ScanFindings scan={scan} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Briefings tab */}
          {tab === 'briefings' && (
            briefings.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No briefings on record yet</div>
                <div style={{ fontSize: 12 }}>Your monthly security briefing will appear here once generated. Briefings are prepared automatically each month.</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  {briefings.length} briefing{briefings.length > 1 ? 's' : ''} on record
                </div>
                {[...briefings].reverse().map((b, i) => {
                  const lines = b.content.split('\n');
                  const headline = lines[0];
                  const body = lines.slice(2).join('\n').trim();
                  return (
                    <div key={i} style={{ ...s.scanRow, marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <div style={s.scanTitle}>{headline}</div>
                        <div style={s.scanDate}>{fmt(b.timestamp)}</div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{body}</div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Groups Active in Your Region tab */}
          {tab === 'threats' && (
            watchlist.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No active threats identified in your region at this time</div>
                <div style={{ fontSize: 12 }}>
                  No active threats identified in your region at this time. Monitoring continues on your behalf.
                </div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 }}>
                  Groups Active in Your Region
                </div>
                {/* Disclaimer */}
                <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                    These groups are not targeting you personally. They are active in your region and industry.
                    We monitor them so you stay informed and protected.
                  </div>
                </div>

                {watchlist.map(actor => {
                  const lvl = (actor.threat_level || 'MEDIUM').toUpperCase();
                  const levelLabel = { CRITICAL: 'Active Threat', HIGH: 'Elevated', MEDIUM: 'Monitor', LOW: 'Awareness' }[lvl] || lvl;
                  const levelColor = { CRITICAL: 'var(--red)', HIGH: 'var(--amber)', MEDIUM: 'var(--blue)', LOW: 'var(--text-muted)' }[lvl] || 'var(--text-muted)';
                  const levelBg = { CRITICAL: 'rgba(239,68,68,0.1)', HIGH: 'rgba(245,158,11,0.1)', MEDIUM: 'rgba(59,130,246,0.1)', LOW: 'rgba(156,163,175,0.08)' }[lvl] || 'transparent';

                  const displayName = actor.aliases?.length > 0
                    ? `${actor.name} (also known as ${actor.aliases[0]})`
                    : actor.name;

                  const summary = actor.executive_summary || actor.description?.slice(0, 200) || '';
                  const methods = actor.attack_methods || [];
                  const action = actor.recommended_action || '';

                  return (
                    <div key={actor.id} style={{ background: 'var(--bg-surface)', border: `0.5px solid ${levelColor}44`, borderRadius: 10, padding: '16px', marginBottom: 12 }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{displayName}</div>
                        <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 10, background: levelBg, border: `0.5px solid ${levelColor}`, color: levelColor, fontWeight: 600, flexShrink: 0 }}>
                          {levelLabel}
                        </span>
                      </div>

                      {/* Who they are */}
                      {summary && (
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>
                          {summary}
                        </div>
                      )}

                      {/* How they operate */}
                      {methods.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 6 }}>HOW THEY OPERATE</div>
                          <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.8 }}>
                            {methods.slice(0, 4).map((m, idx) => <li key={idx}>{m}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Recommended action */}
                      {action && (
                        <div style={{ background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <span style={{ fontSize: 15, flexShrink: 0 }}>🛡</span>
                          <div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 3 }}>WHAT TO DO</div>
                            <div style={{ fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.6 }}>{action}</div>
                          </div>
                        </div>
                      )}

                      {actor.last_active && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Last known activity: {actor.last_active}</div>
                      )}
                    </div>
                  );
                })}

                {/* Closing note */}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7, marginTop: 8, padding: '12px 14px', background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 8, fontStyle: 'italic' }}>
                  No immediate action required. Contact your concierge if anything seems relevant to you.
                </div>
              </div>
            )
          )}

          {/* Timeline tab */}
          {tab === 'timeline' && (
            chartData.length === 0 ? (
              <div style={s.empty}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>No score history yet</div>
                <div style={{ fontSize: 12 }}>
                  Your score timeline will build up after assessments and breach checks run
                </div>
              </div>
            ) : (
              <div>
                {/* Chart */}
                <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px 8px 8px', marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.07em', paddingLeft: 10, marginBottom: 12 }}>
                    RISK SCORE — LAST {chartData.length} READINGS
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="shortDate"
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        axisLine={false}
                        tickLine={false}
                        ticks={[0, 25, 50, 75, 100]}
                      />
                      <Tooltip content={<ScoreTooltip />} cursor={{ stroke: 'var(--border)', strokeWidth: 1 }} />
                      {/* Grade zone reference lines */}
                      <ReferenceLine y={75} stroke="#22c55e" strokeDasharray="4 3" strokeOpacity={0.4} />
                      <ReferenceLine y={50} stroke="#ef4444" strokeDasharray="4 3" strokeOpacity={0.4} />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#c8b48a"
                        strokeWidth={2}
                        dot={<ScoreDot />}
                        activeDot={{ r: 7, stroke: 'var(--gold)', strokeWidth: 2, fill: 'var(--bg-base)' }}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {/* Legend */}
                  <div style={{ display: 'flex', gap: 14, paddingLeft: 10, marginTop: 8 }}>
                    {[['#22c55e', '75+ Strong'], ['#eab308', '50–74 Moderate'], ['#ef4444', '<50 At risk']].map(([c, l]) => (
                      <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Factors breakdown for latest score */}
                {latestRisk && (
                  <div style={{ background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.07em', marginBottom: 2 }}>
                      CURRENT SCORE BREAKDOWN
                    </div>
                    <FactorsBreakdown
                      factors={latestRisk.factors}
                      score={latestRisk.score}
                      grade={latestRisk.grade}
                    />
                  </div>
                )}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
